<template>
  <div
    class="admin-layout-wrapper relative"
    :class="{ 'menu-open': isMenuOpen }">
    <div
      class="admin-header bg-gray-5 c-bg box-shadow-4 flex gap-m flex-middle"
      style="border-bottom: 1px solid var(--c-gray-2)">
      <div
        class="side-spacer flex-start flex-middle gap-l padding-l-horizontal" >

        <icon
          v-show="!isMenuOpen"
          class="animate__fadeIn animate--faster cursor-pointer"
          size="2"
          icon="align-justify-alt"
          @click="isMenuOpen = !isMenuOpen"></icon>
        <icon
          v-show="isMenuOpen"
          class="animate__fadeIn animate--faster cursor-pointer"
          size="2"
          icon="x"
          @click="isMenuOpen = !isMenuOpen"></icon>
        <div
          class="h1 margin-remove margin-auto c-bg text-overflow-ellipsis cursor-pointer m:visible"
          style="font-size: calc(var(--font-2xl) * 1.2)"
          @click="$router.push('/')">
          {{ translate("core.meta.siteName") }}
        </div>
      </div>
      <div class="widgets-wrapper width-expand">
        <div class="inner flex gap-xl">
          <drawer
            v-model="isShowingWidgets"
            style="transition: transform 200ms ease-out"
            theme="gray-5"
            :showCloseIcon="false"
            :width="`calc(var(--base-margin) * 66)`"
            location="inline-end">
            <template #drawer-content>
              <div
                class="widgets flex-column gap-l flex-stretch padding-l-top"
                style="margin-inline-start: auto">
                <language-switcher
                  style="
                    max-width: 30px;
                    max-height: 30px;
                    aspect-ratio: 1;
                  "></language-switcher>

                <div class="notification-widget flex-middle">
                  <icon-halo
                    theme="bg"
                    icon-theme="gray-5"
                    class="c-gray-5"
                    @click="isShowingWidgets = false">
                    <template #icon>
                      <notification-widget
                        :icon-props="{ size: 1 }"
                        :badge-props="{
                          style: 'top: -1.25em; inset-inline-end: -1.25em;',
                        }"
                        :dropdown-props="{
                          class: 'c-text',
                        }"></notification-widget>
                    </template>
                  </icon-halo>
                </div>
                <div class="chat-widget flex-middle cursor-pointer">
                  <saffron-link :to="{ name: 'admin-my-conversation' }">
                    <icon-halo
                      @click="isShowingWidgets = false"
                      halo-theme="bg"
                      theme="gray-5"
                      :size="1"
                      :halo-ratio="0.75"
                      icon="chat-message"
                      :title="tu('admin.widgets.tooltips.chat')"></icon-halo>
                  </saffron-link>
                </div>
                <div class="home-widget flex-middle cursor-pointer">
                  <router-link :to="`/`">
                    <icon-halo
                      @click="isShowingWidgets = false"
                      halo-theme="bg"
                      theme="gray-5"
                      :size="1"
                      :halo-ratio="0.75"
                      icon="home"
                      :title="tu('admin.widgets.tooltips.home')"></icon-halo>
                  </router-link>
                </div>
                <div
                  class="logout-widget flex-middle cursor-pointer"
                  @click="logout">
                  <icon-halo
                    @click="isShowingWidgets = false"
                    halo-theme="bg"
                    theme="gray-5"
                    :size="1"
                    :halo-ratio="0.75"
                    :title="tu('admin.widgets.tooltips.logout')"
                    icon="arrow-logout-inline-end"></icon-halo>
                </div>
              </div>
            </template>
          </drawer>
          <div
            class="end-widgets flex gap-l flex-stretch"
            style="margin-inline-start: auto">
            <div
              class="profile-widget flex gap-m flex-middle bg-bg cursor-pointer"
              @click="goToProfile">
              <user-avatar size="xs" v-bind="userProfile"></user-avatar>
              <icon theme="gray-5" icon="pencil" :size="1"></icon>
            </div>

            <div class="home-widget flex-middle cursor-pointer">
              <router-link :to="`/`">
                <icon-halo
                  halo-theme="bg"
                  theme="gray-5"
                  :size="1"
                  :halo-ratio="0.75"
                  icon="home"
                  :title="tu('admin.widgets.tooltips.home')"></icon-halo>
              </router-link>
            </div>

            <icon-halo
              class="cursor-pointer"
              halo-theme="bg"
              theme="gray-5"
              :size="1"
              :halo-ratio="0.75"
              @click="isShowingWidgets = true"
              icon="menu-grid"></icon-halo>
          </div>
        </div>
      </div>
    </div>
    <div
      class="background-bg-4 admin-outer-grid flex gap-m relative padding-2xl-bottom">
      <div
        v-show="isMenuOpen"
        ref="sideMenuContainer"
        :class="{
          'being-sticky': isNavBeingSticky,
          animate__fadeInRight: isLanguageRtl(),
          animate__fadeInLeft: !isLanguageRtl(),
        }"
        class="container side-menu-container container-s padding-m padding-remove-bottom animate--fastest">
        <div
          class="radius-s padding-m bg-bg flex-column relative side-menu-inner padding-l-bottom">
          <div
            class="logo flex flex-center flex-middle margin-l-top margin-xl-bottom cursor-pointer"
            @click="$router.push({ name: 'admin' })">
            <img
              :src="getDynamicAssetUrl('images/logos/main.png')"
              style="height: 50px; width: 50px" />
          </div>

          <base-side-nav
            v-if="shouldShowSideNav"
            style="height: 100%; max-height: 100%"
            :items="sideNavLinks">
          </base-side-nav>
        </div>
      </div>
      <div class="main-content-wrapper width-expand">
        <slot name="default"></slot>
      </div>
    </div>
    <div class="bottom-bar">
      <div class="margin-inline-auto padding-s-vertical padding-m-horizontal">
        <div class="actions flex gap-m flex-wrap">
          <form-button
            icon="trash"
            size="s"
            theme="gray-4"
            :loading="isClearingCache"
            text="admin.bottomBar.clearCache"
            @click="clearCache"></form-button>
          <form-button
            icon="refresh"
            size="s"
            theme="danger"
            :loading="isRestartingServer"
            :disabled="restartServerTimedOut"
            text="admin.bottomBar.restartServer"
            @click="startRestartServerFlow"></form-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import BaseSideNav from "@/client/components/nav/BaseSideNav.vue";
import asyncOperations from "@/client/extensions/composition/asyncOperations.js";
import { useStore } from "vuex";
import UserAvatar from "@/client/components/chat/UserAvatar";
import NotificationWidget from "@/client/components/etc/NotificationWidget";
import LanguageSwitcher from "@/client/components/base/frameworks/we/etc/LanguageSwitcher.vue";
//import PrimarySearch from "@/client/applications/justopp/components/widgets/PrimarySearch";
export default {
  name: "AdminDashboardLayout",
  components: { LanguageSwitcher, BaseSideNav, UserAvatar, NotificationWidget }, //PrimarySearch
  props: {
    defaultComponent: String,
    logo: {
      type: String,
      default: "logos/main.png",
    },
  },
  setup: function (props) {
    let { asyncOps, asyncOpsReady } = asyncOperations(props, useStore());

    return { asyncOps, asyncOpsReady };
  },
  data: function () {
    return {
      serverRestartErrorTimeout: null,
      restartServerTimedOut: false,
      isMenuOpen: true,
      sideNavLinks: false,
      isClearingCache: false,
      isRestartingServer: false,
      cacheClearButtonResetTimeout: null,
      isNavBeingSticky: false,
      socket: false,
      shouldShowSideNav: false,
      isShowingWidgets: false,
    };
  },
  computed: {
    userProfile() {
      return this.$store.getters["user/profile"];
    },
    isLoginStatusKnown() {
      return computed(() => this.$store.getters["user/isTokenValid"] !== null);
    },
    isLoggedIn() {
      return computed(() => this.$store.getters["user/isTokenValid"]);
    },
    logoStyle() {
      let url = utilities.requireAsset("images/" + this.logo);
      return {
        backgroundImage: "url('" + url + "')",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center 5px",
        height: "78px",
        minHeight: "0",
        backgroundSize: "60px auto",
      };
    },
  },
  created() {
    let handler = async () => {
      // why delaying the menu is required we do not know. but vue blows up.
      // so do not render the menu in SSR and delay it's loading on client
      if (utilities.isSSR()) {
        return;
      }
      let navFactory = await this.getDynamicAsset(
        `core/nav/adminSideNavFactory.js`
      );
      let getter =
        typeof navFactory === "function" ? navFactory : navFactory.default;

      let items = await getter({
        asyncOperations: this.asyncOps,
        store: this.$store,
      });

      this.sideNavLinks = items;

      utilities.setClientTimeout(() => {
        this.shouldShowSideNav = true;
      }, 100);
    };

    handler();
  },
  async mounted() {
    // should never happen - no mount on server. but still.
    if (utilities.isSSR()) {
      return;
    }

    if (!this.socket) {
      this.socket = await this.asyncOps.socket.init();
      await this.socket.waitForUUID();
    }

    this.socket.on("allCacheCleared", this.allCacheClearedHandler);

    this.socket.on("allCacheClearError", this.allCacheClearErrorHandler);

    this.socket.on("serverOnline", this.serverOnlineHandler);

    this.socket.on("serverRestartFailed", this.serverResetErrorHandler);
    //serverResetErrorHandler
    window.addEventListener("scroll", this.scrollHandler, { passive: true });
  },
  beforeUnmount() {
    this.socket.off("allCacheCleared", this.allCacheClearedHandler);

    this.socket.off("allCacheClearError", this.allCacheClearErrorHandler);

    this.socket.off("serverOnline", this.serverOnlineHandler);

    this.socket.off("serverRestartFailed", this.serverResetErrorHandler);
    window.removeEventListener("scroll", this.scrollHandler, { passive: true });
  },
  methods: {
    async startRestartServerFlow() {
      try {
        let res = await this.$s.ui.modal.prompt(
          this.t("admin.restartServer.serverRestartConfirmText"),
          this.tt("admin.restartServer.serverRestartConfirmTitle"),
          {
            contentProps: {
              class: "bold c-danger",
            },
            confirmButtonProps: {
              theme: "danger",
            },
          }
        );
        if (res === 'RESTART') {
          this.restartServer();
        } else {
          this.$s.ui.notification(
            "admin.restartServer.canceledBadInput",
            "info"
          );
        }
      } catch(e) {
        this.$s.ui.notification(
          "admin.restartServer.canceled",
          "info"
        );
      }

    },
    async restartServer() {
      this.isRestartingServer = true;
      this.serverRestartErrorTimeout = setTimeout(() => {
        this.restartServerTimedOut = true;
      }, 20000);
      let res = this.asyncOps.asyncCall(
        "actions/server/restart",
        {},
        { method: "post" }
      );
      if (res.hasError) {
        this.$s.ui.notification(
          "admin.serverStart.restartRequestError",
          "error"
        );
      }
    },
    logout() {
      this.$store.commit("user/invalidateAuth");
      this.$store.commit("user/clearProfile");
      this.$router.push("/");
    },
    goToProfile() {
      this.$router.push({
        name: "admin-entity-entityType-edit-entityId",
        params: {
          entityType: "user",
          entityId: this.userProfile.id,
        },
      });
    },
    async clearCache() {
      clearTimeout(this.cacheClearButtonResetTimeout);
      this.isClearingCache = true;
      this.$s.ui.notification(
        "admin.cache.clearCacheStartNotification",
        "info"
      );
      setTimeout(() => {
        this.isClearingCache = false;
      }, 120000);
      this.asyncOps.asyncCall("cache", {}, { method: "delete" });
    },
    scrollHandler() {
      try {
        let bounding = this.$refs.sideMenuContainer.getBoundingClientRect();
        this.isNavBeingSticky = bounding.top <= 0;
      } catch (e) {
        // try catch required - dismount could have happened
      }
    },
    serverResetErrorHandler() {
      clearTimeout(this.serverRestartErrorTimeout);
      this.isRestartingServer = false;
      this.restartServerTimedOut = false;
      this.$s.ui.notification(
        "admin.restartServer.restartRequestError",
        "error"
      );
    },
    serverOnlineHandler() {
      clearTimeout(this.serverRestartErrorTimeout);
      this.isRestartingServer = false;
      this.restartServerTimedOut = false;
    },
    allCacheClearErrorHandler() {
      this.isClearingCache = false;
      this.$s.ui.notification(
        "admin.cache.clearCacheErrorNotification",
        "error"
      );
    },
    allCacheClearedHandler() {
      this.isClearingCache = false;
      this.$s.ui.notification(
        "admin.cache.clearCacheCompleteNotification",
        "success"
      );
    },
  },
};
</script>

<style scoped lang="scss">
.admin-layout-wrapper {
  --side-menu-width: 220px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.admin-outer-grid {
  flex-direction: row;
  flex: 100%;
}

.widgets-wrapper {
  padding-block: var(--margin-m);
  padding-inline: var(--margin-l);

  @media (max-width: 767px) {
    padding-inline-start: 0;
  }
}

.side-spacer {
  width: 220px;
  flex: 0 0 220px;
  @media (max-width: 1024px) {
    width: auto;
    flex: auto;
  }
}

.side-menu-container,
.container.side-menu-container {
  width: var(--side-menu-width);
  flex: 0 0 var(--side-menu-width);
  padding-inline-end: 0;

  .side-menu-inner {
    height: calc(100vh - 100px);
    position: sticky;
    top: var(--margin-m);
  }

  &.being-sticky {
    .side-menu-inner {
      height: calc(100vh - 2 * var(--margin-m));
    }
  }
}

.side-menu {
  width: 100%;
  flex: 100%;

  .side-menu-logo {
    display: block;
    height: 100px;
    width: 100px;
    margin: var(--margin-xl) auto var(--margin-l) auto;
  }

  .side-nav {
    list-style: none;
    padding-inline-start: 20px;
  }
}

.main-content-wrapper {
  max-width: 100%;

  .menu-open & {
    max-width: calc(100vw - 2 * var(--margin-m) - var(--side-menu-width));
  }
}

.admin-notification-widget {
  .notification-icon {
    position: relative;
    cursor: pointer;

    .notification-badge {
      position: absolute;
      top: -70%;
      right: -50%;
      z-index: 2;
    }
  }
}

.profile-widget {
  border-radius: var(--border-radius-l);
  padding-inline-end: var(--margin-m);
}

.bottom-bar {
  position: sticky;
  bottom: 0;
  inset-inline-start: 0;
  width: 100%;
  z-index: var(--z-4);
  background-color: var(--c-gray-5);
  border-top: 1px solid var(--c-gray-2);
  box-shadow: var(--box-shadow-2);
}
</style>
